import 'leaflet/dist/leaflet.css';
import 'leaflet-polylinedecorator';

import { useContext, useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, useMapEvents, ZoomControl } from 'react-leaflet';

import { useTargetTracking } from '@/hooks/useTargetTracking'; // FIXME @/hooks
import { loadSavedPosition, SavePositionComponent } from './MapPosition';
import { RoutesBoundaryComponent } from './RoutesBoundaryComponent';
import { TrackingBoundaryComponent } from './TrackingBoundaryComponent';
import { TargetMarkers } from './TargetMarkers';
import { ctxPluto } from '@/contexts/PlutoContext';
import { Routes } from './Route';
import { useRouteLoader } from '@/hooks/useRouteLoader';
import { YouAreHere } from './YouAreHere';
import { LoadTargetToken } from './LoadTargetToken';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { ctxRoutes } from '@/contexts/RoutesCtx';
import { TrackedPathPolyline } from './TrackedPathPolyline';
import { Position } from '@/tmp/Target';
import CreateRoutesPanes from './CreateRoutesPanes';
import TargetTrackingPanel from '@/components/tracking/TargetTrackingPanel';

import './Map.css';

export default function PlutoMap() {
  const [isPhone, setPhone] = useState<boolean>(isPhoneOrHighScreen);
  const ctx = useContext(ctxPluto);
  const routesCtx = useContext(ctxRoutes);
  // csak az egyik kell....
  const [highlightedRoutes, grayRoutes] = useRouteLoader();
  const fullTargets = useTargetTracking();
  const [selectedTargetId, setSelectedTargetId] = useState<number | undefined>();
  const routesMapRef = useRef<L.Map | null>(null);
  const targetMapRef = useRef<L.Map | null>(null);
  const [zoomLevel, setZoomLevel] = useState(7);

  function ZoomHandler() {
    // eslint-disable-next-line react-compiler/react-compiler
    const map = useMapEvents({
      zoomend: () => {
        setZoomLevel(map.getZoom());
      },
    });
    // eslint-disable-next-line react-compiler/react-compiler
    useEffect(() => {
      if (!map) return;
      setZoomLevel(map.getZoom());
    }, [map]);
    return null;
  }

  // ez jojjon kivulrol
  useEffect(() => {
    function handleResize() {
      setPhone(isPhoneOrHighScreen);
    }

    // TODO lehetne mediaquery
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  useEffect(() => {
    ctx?.setFullTargets(fullTargets); // FIXME lehet inkabb a hook csinalja ?
  }, [ctx, fullTargets]);

  useEffect(() => {
    if (!ctx || !targetMapRef.current) return;
    if (isPhoneOrHighScreen() && ctx.popupTargetId) {
      if (ctx.isBottomSheetOpen) {
        targetMapRef.current.getContainer().style.height = 'calc(100% - 260px)';
      } else {
        targetMapRef.current.getContainer().style.height = 'calc(100% - 43px)';
      }
    } else targetMapRef.current.getContainer().style.height = '100%';
  }, [ctx, ctx?.popupTargetId]);

  useEffect(() => {
    if (!routesCtx || !routesMapRef.current) return;
    if (isPhoneOrHighScreen() && routesCtx.isBottomSheetOpen) {
      if (routesCtx.isBottomSheetUp) {
        routesMapRef.current.getContainer().style.height = 'calc(100% - 260px)';
      } else {
        routesMapRef.current.getContainer().style.height = 'calc(100% - 43px)';
      }
    } else routesMapRef.current.getContainer().style.height = '100%';
  }, [routesCtx, routesCtx?.isBottomSheetUp, routesCtx?.isBottomSheetOpen]);

  const [zoom, center] = loadSavedPosition();
  const maxNativeZoom = 18;
  const maxZoom = 22;

  let trackedPath: Position[] | undefined = undefined;
  if (fullTargets) {
    const popupFullTarget = fullTargets.find((entry) => entry.target.id === ctx?.popupTargetId);
    trackedPath = popupFullTarget ? popupFullTarget.trackedPath : undefined;
  }

  return (
    <>
      {ctx?.mode === 'route' && (
        <div className="map" data-qa-element-id="map_panel">
          <MapContainer
            className="map"
            ref={routesMapRef}
            center={center}
            zoom={zoom}
            scrollWheelZoom={true}
            zoomControl={false}
            style={{ zIndex: 1 }}
          >
            <CreateRoutesPanes />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="/osm/{z}/{x}/{y}.png"
              maxNativeZoom={maxNativeZoom}
              maxZoom={maxZoom}
            />
            {!isPhoneOrHighScreen() && <ZoomControl position="bottomright" />}
            <Routes
              highlightedRoutes={highlightedRoutes}
              grayRoutes={grayRoutes}
              zoomSize={Math.pow(10, -(zoomLevel - 5))}
            />
            <RoutesBoundaryComponent fullRoutes={highlightedRoutes} />
            <ZoomHandler />
          </MapContainer>
        </div>
      )}
      {ctx?.mode === 'target' && (
        <>
          {!isPhone && <TargetTrackingPanel activeMenuIndex={'1'} />}
          <div className="map" data-qa-element-id="map_panel">
            <MapContainer
              className="map"
              ref={targetMapRef}
              center={center}
              zoom={zoom}
              preferCanvas
              scrollWheelZoom={true}
              zoomControl={false}
              style={{ zIndex: 1, height: ctx.popupTargetId && isPhoneOrHighScreen() ? 'calc(100% - 260px)' : '100%' }}
            >
              <SavePositionComponent />
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="/osm/{z}/{x}/{y}.png"
                maxNativeZoom={maxNativeZoom}
                maxZoom={maxZoom}
              />
              {!isPhoneOrHighScreen() && <ZoomControl position="bottomright" />}
              <YouAreHere />
              <LoadTargetToken />
              <TargetMarkers
                fullTargets={fullTargets}
                selectedTargetId={selectedTargetId}
                setSelectedTargetId={setSelectedTargetId}
              />
              <TrackingBoundaryComponent
                fullTargets={fullTargets}
                selectedTargetId={selectedTargetId}
                setSelectedTargetId={setSelectedTargetId}
              />
              {trackedPath && trackedPath.length > 0 && <TrackedPathPolyline positions={trackedPath} />}
            </MapContainer>
          </div>
        </>
      )}
    </>
  );
}
